import { toast } from "@indigo-components";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { useAtom } from "jotai";
import { useCallback, useRef } from "react";
import {
  defaultImpersonationState,
  IMPERSONATION_STATE_KEY,
  ImpersonationState,
  ImpersonationTargetType,
  impersonationAtom,
} from "~/atoms/impersonation";
import { getIdToken } from "~/auth/authToken";
import {
  backupAdminSession,
  clearCognitoLocalStorageKeys,
  getCurrentIdTokenPayload,
  hasAdminSessionBackup,
  restoreAdminSession,
} from "~/auth/sessionBackup";
import { getCurrentEnv } from "~/common/utils";
import { errorReporter } from "~/error-handling";
import { useServices } from "~/hooks/useServices";

const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes
const PENDING_IMPERSONATION_KEY = "impersonation_pending";
// Surfaced to the admin on the next boot after a failed/aborted assume so the
// restore-to-admin doesn't look like an unexplained logout. Consumed by
// consumeImpersonationError() once a live session is confirmed.
const IMPERSONATION_ERROR_KEY = "impersonation_error";
// Drop an error flag older than this — it belongs to a past event, not the
// session that's now booting. Bounds staleness when the flag is set but the
// consuming session (see AuthSessionProvider) doesn't come up promptly.
const IMPERSONATION_ERROR_TTL_MS = 5 * 60 * 1000;
// One-time challenge token TTL — must stay >= the backend's challengeExpiresAt
// (+5 min in lambdas/impersonation/handler.py). A pending blob older than this
// can never complete, so we skip the (cold-start-slow) retry and restore now.
const CHALLENGE_TTL_MS = 5 * 60 * 1000;
// Dev-only: when set, the next assume completion aborts to exercise the
// recovery path. Armed via window.__simulateImpersonationFailure(). Never
// honored outside dev (see completePendingImpersonation gate).
const FORCE_FAIL_KEY = "impersonation_force_fail_dev";

/**
 * Dev-only test hook: make the NEXT assume attempt abort at completion so the
 * failAndRestore recovery (restore admin + toast + redirect) can be verified in
 * the browser without forcing a real lambda cold-start/timeout. No-op outside
 * dev. Wired to window.__simulateImpersonationFailure() in AuthSessionProvider.
 */
export function armImpersonationFailureForTest(): void {
  if (getCurrentEnv() !== "dev") return;
  sessionStorage.setItem(FORCE_FAIL_KEY, "1");
}

interface PendingImpersonation {
  sessionId: string;
  challengeToken: string;
  targetUsername: string;
  adminCognitoSub: string;
  targetId: string;
  targetType: ImpersonationTargetType;
  reason: string;
  // ISO timestamp set when startImpersonation stored the blob. Optional so a
  // blob written by an older bundle mid-deploy still parses (treated as
  // unknown-age → we attempt completion rather than fast-failing).
  createdAt?: string;
}

interface ImpersonationError {
  message: string;
  at: string;
}

/** Store the one-shot "impersonation failed/ended" message with a timestamp. */
function setImpersonationError(message: string): void {
  sessionStorage.setItem(
    IMPERSONATION_ERROR_KEY,
    JSON.stringify({ message, at: new Date().toISOString() }),
  );
}

/**
 * Read and clear the one-shot "impersonation failed" flag set when an assume
 * aborted (and the admin was restored) or an end couldn't restore the admin.
 * Returns a user-facing message, or null if there's none or it's stale.
 *
 * Callers should only invoke this once a live session is confirmed (see
 * AuthSessionProvider) — otherwise a hard redirect (e.g. the restored admin
 * token is itself invalid) would consume the flag before <Toaster/> mounts,
 * dropping the message with no way to show it again.
 */
export function consumeImpersonationError(): string | null {
  const raw = sessionStorage.getItem(IMPERSONATION_ERROR_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(IMPERSONATION_ERROR_KEY);
  try {
    const { message, at } = JSON.parse(raw) as ImpersonationError;
    const ageMs = Date.now() - new Date(at).getTime();
    if (!Number.isFinite(ageMs) || ageMs > IMPERSONATION_ERROR_TTL_MS) {
      return null;
    }
    return message;
  } catch {
    return null;
  }
}

/**
 * Page the admin returns to after impersonation ends or aborts: the
 * physician / MA detail page that hosts the Assume button. Kept as one helper
 * so completePendingImpersonation and endImpersonation stay in sync.
 */
function impersonationReturnPath(
  targetType: ImpersonationTargetType,
  targetId: string,
): string {
  return targetType === "MEDICAL_ASSISTANT"
    ? `/ops-settings/${targetId}`
    : `/physicians/${targetId}`;
}

/**
 * Abort a half-completed impersonation: restore the admin's session (if we
 * still hold a backup) and land them back as themselves with an explanation,
 * instead of stranded on the login page. This is the core recovery guarantee —
 * a broken assume must never silently log the admin out.
 *
 * When we know the intended target, a restored admin returns to the page they
 * started from (same as ending an assume); otherwise we fall back to "/".
 */
function failAndRestore(
  reason: string,
  { err, pending }: { err?: unknown; pending?: PendingImpersonation } = {},
): false {
  sessionStorage.removeItem(PENDING_IMPERSONATION_KEY);

  const hadBackup = hasAdminSessionBackup();
  const restored = hadBackup && restoreAdminSession();

  errorReporter.report(
    err instanceof Error ? err : new Error(`Impersonation aborted: ${reason}`),
    {
      // A restored admin is a recoverable annoyance; a missing backup means the
      // admin is genuinely logged out and can't recover — page that louder.
      level: restored ? "warning" : "error",
      tags: {
        component: "impersonation",
        operation: "complete-pending",
        outcome: restored ? "restored-admin" : "logged-out",
      },
      extra: { reason, hadBackup },
    },
  );

  setImpersonationError(
    restored
      ? "Couldn't start impersonation — you're back in your own account. Please try again."
      : "Impersonation failed and your original session couldn't be restored. Please sign in again.",
  );

  window.location.href =
    restored && pending
      ? impersonationReturnPath(pending.targetType, pending.targetId)
      : "/";
  return false;
}

/**
 * Evict an impersonation that lapsed while no tab was open (browser closed
 * without ending it). The banner countdown never fired, so the target's Amplify
 * tokens are still in localStorage; once the idToken ages out, a refresh mints
 * a plain target token with no impersonation claims and the admin silently
 * stays signed in as the target. Must run before Amplify reads the session.
 * Returns true if a lapsed impersonation was evicted.
 */
export function expireLapsedImpersonation(): boolean {
  const raw = localStorage.getItem(IMPERSONATION_STATE_KEY);
  if (!raw) return false;

  let stored: ImpersonationState;
  try {
    stored = JSON.parse(raw) as ImpersonationState;
  } catch {
    return false;
  }
  if (!stored.expiresAt || new Date(stored.expiresAt) >= new Date()) {
    return false;
  }

  localStorage.removeItem(IMPERSONATION_STATE_KEY);
  const restored = restoreAdminSession();
  // No backup: same split as endImpersonation's step 2b. Wipe only if the live
  // token is still impersonated (it carries the claim until refreshed, and
  // nothing has refreshed yet at boot); a plain session means the admin was
  // already restored before the state key was cleared — keep it.
  const stillImpersonating =
    !restored && !!getCurrentIdTokenPayload()?.["custom:impersonator_sub"];
  if (stillImpersonating) clearCognitoLocalStorageKeys();

  errorReporter.report(new Error("Impersonation lapsed while tab closed"), {
    level: "warning",
    tags: {
      component: "impersonation",
      operation: "expire-lapsed",
      outcome: restored
        ? "restored-admin"
        : stillImpersonating
          ? "logged-out"
          : "already-admin",
    },
    extra: { sessionId: stored.sessionId, expiresAt: stored.expiresAt },
  });
  if (restored) {
    setImpersonationError(
      "Your impersonation session expired — you're back in your own account.",
    );
  } else if (stillImpersonating) {
    setImpersonationError(
      "Your impersonation session expired. Please sign in again.",
    );
  }
  return true;
}

// Re-entry guard: completePendingImpersonation runs once at module load, but
// guard anyway against a double invocation racing two sign-in attempts.
let completionInFlight = false;

/**
 * Complete a pending impersonation sign-in after a page reload.
 * Called early in the app lifecycle (before Authenticator renders its login UI).
 * Returns true if a pending impersonation was found and completed.
 */
export async function completePendingImpersonation(): Promise<boolean> {
  const raw = sessionStorage.getItem(PENDING_IMPERSONATION_KEY);
  if (!raw) return false;

  if (completionInFlight) return false;
  completionInFlight = true;

  let pending: PendingImpersonation;
  try {
    pending = JSON.parse(raw) as PendingImpersonation;
  } catch (err) {
    // Corrupt blob — can't complete. Restore the admin rather than stranding.
    // No target info available, so this lands back at "/".
    return failAndRestore("corrupt-pending-blob", { err });
  }

  // A stale blob's one-time challenge token can no longer be redeemed, so a
  // retry is guaranteed to fail after several cold-start-slow lambda hops.
  // Skip it and restore the admin immediately.
  if (pending.createdAt) {
    const ageMs = Date.now() - new Date(pending.createdAt).getTime();
    if (ageMs > CHALLENGE_TTL_MS)
      return failAndRestore("challenge-expired", { pending });
  }

  // Dev-only: simulate a mid-flow abort to exercise the recovery path. Runs
  // here (after the real backup + pending were set by startImpersonation) so
  // it drives the genuine restore/toast/redirect, not a stubbed one.
  if (getCurrentEnv() === "dev" && sessionStorage.getItem(FORCE_FAIL_KEY)) {
    sessionStorage.removeItem(FORCE_FAIL_KEY);
    return failAndRestore("dev-forced-failure", { pending });
  }

  try {
    // Sign in as target user using CUSTOM_AUTH flow
    const signInResult = await signIn({
      username: pending.targetUsername,
      options: {
        authFlowType: "CUSTOM_WITHOUT_SRP",
        clientMetadata: {
          impersonation: "true",
          sessionId: pending.sessionId,
        },
      },
    });

    // Answer the custom challenge with the one-time token. Any other next step
    // means the CUSTOM_AUTH flow didn't start as expected — treat as a failure
    // so we restore rather than proceed with no target session.
    if (
      signInResult.nextStep.signInStep !==
      "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
    ) {
      return failAndRestore(
        `unexpected-signin-step:${signInResult.nextStep.signInStep}`,
        { pending },
      );
    }

    const confirmResult = await confirmSignIn({
      challengeResponse: pending.challengeToken,
      options: {
        clientMetadata: {
          impersonation: "true",
          sessionId: pending.sessionId,
        },
      },
    });

    // Verify a real target session was established before we commit the swap
    // and drop the admin. Guards the case where the challenge response resolves
    // without actually signing in (e.g. a trigger aborted server-side).
    if (!confirmResult.isSignedIn) {
      return failAndRestore("confirm-not-signed-in", { pending });
    }

    // Set impersonation state in localStorage (atom will hydrate from this)
    const now = new Date();
    const impersonationState: ImpersonationState = {
      isActive: true,
      sessionId: pending.sessionId,
      adminCognitoSub: pending.adminCognitoSub,
      targetUsername: pending.targetUsername,
      targetType: pending.targetType,
      physicianId: pending.targetType === "DOCTOR" ? pending.targetId : null,
      medicalAssistantId:
        pending.targetType === "MEDICAL_ASSISTANT" ? pending.targetId : null,
      reason: pending.reason,
      startedAt: now.toISOString(),
      expiresAt: new Date(now.getTime() + SESSION_DURATION_MS).toISOString(),
    };
    localStorage.setItem(
      IMPERSONATION_STATE_KEY,
      JSON.stringify(impersonationState),
    );

    // Clean up pending flag
    sessionStorage.removeItem(PENDING_IMPERSONATION_KEY);

    // Hard navigate to reinitialize the app with the target user's session
    window.location.href = "/";
    return true;
  } catch (err) {
    // Any failure after the admin's tokens were cleared (throttle, cold-start
    // trigger timeout, network) restores the admin instead of logging them out.
    return failAndRestore("signin-threw", { err, pending });
  }
}

export function useImpersonation() {
  const [state, setState] = useAtom(impersonationAtom);
  const { lambda } = useServices();
  const isStartingRef = useRef(false);
  const isEndingRef = useRef(false);

  const startImpersonation = useCallback(
    async (
      targetId: string,
      targetType: ImpersonationTargetType,
      reason: string,
    ) => {
      if (isStartingRef.current || !lambda) return;
      isStartingRef.current = true;

      try {
        // 1. Get admin's ID token for the impersonation lambda (user-pool only)
        const idToken = await getIdToken();

        // 2. Call impersonation lambda to get challenge token
        const result = await lambda.initiateImpersonation(
          targetId,
          targetType,
          reason,
          idToken,
        );
        if (!result.data) {
          throw new Error(
            result.error?.message || "Failed to initiate impersonation",
          );
        }
        const {
          session_id: sessionId,
          challenge_token: challengeToken,
          target_username: targetUsername,
          admin_cognito_sub: adminCognitoSub,
        } = result.data;

        // 3. Backup admin's Amplify tokens so we can restore them if the assume
        //    aborts. Refuses if the current session is already an impersonated
        //    one (e.g. another tab swapped the shared session) — clearing tokens
        //    without a real admin backup would strand the admin, so abort the
        //    start instead of proceeding into an unrecoverable state.
        if (!backupAdminSession()) {
          errorReporter.report(
            new Error(
              "startImpersonation: current session is not a plain admin session; aborting",
            ),
            {
              level: "warning",
              tags: {
                component: "impersonation",
                operation: "start",
                outcome: "no-admin-backup",
              },
            },
          );
          toast({
            message:
              "Couldn't start impersonation from this tab. Please retry from a fresh tab.",
            variant: "error",
            duration: 8000,
          });
          return;
        }

        // 4. Store challenge info for completion after page reload
        const pending: PendingImpersonation = {
          sessionId,
          challengeToken,
          targetUsername,
          adminCognitoSub,
          targetId,
          targetType,
          reason,
          createdAt: new Date().toISOString(),
        };
        sessionStorage.setItem(
          PENDING_IMPERSONATION_KEY,
          JSON.stringify(pending),
        );

        // 5. Clear Cognito tokens from localStorage and hard navigate.
        //    The page reload clears Amplify's in-memory state.
        //    completePendingImpersonation() runs on the fresh load to
        //    complete the signIn before React renders.
        clearCognitoLocalStorageKeys();
        window.location.href = "/";
      } finally {
        isStartingRef.current = false;
      }
    },
    [lambda],
  );

  const endImpersonation = useCallback(async () => {
    if (isEndingRef.current) return;
    isEndingRef.current = true;

    const { sessionId, targetType, physicianId, medicalAssistantId } = state;

    try {
      // 1. Call end impersonation lambda (using target user's impersonated JWT,
      //    user-pool only — no Identity Pool call)
      if (lambda && sessionId) {
        const idToken = await getIdToken();
        await lambda.endImpersonation(sessionId, idToken);
      }
    } catch {
      // Best-effort — even if the end call fails, restore admin session
    }

    // 2. Restore Admin's tokens directly (do NOT call signOut to avoid Hub event race)
    const restored = restoreAdminSession();

    // 2b. Restore can fail two very different ways when the backup is gone. The
    //     admin-session backup is a single shared localStorage key, consumed
    //     (deleted) on the first successful restore — so with the same
    //     impersonation open in multiple tabs, a second "End" click finds it
    //     already gone. Distinguish by inspecting whose session is live NOW:
    //       - still an impersonated session → genuinely orphaned; tear it down
    //         so Amplify can't re-hydrate the target, then force a re-login.
    //       - already a plain (admin) session → another tab restored the admin;
    //         do NOT wipe those tokens (that's the concurrent-End logout race).
    const stillImpersonating =
      !restored && !!getCurrentIdTokenPayload()?.["custom:impersonator_sub"];

    if (!restored && stillImpersonating) {
      clearCognitoLocalStorageKeys();
      errorReporter.report(
        new Error("endImpersonation: no admin backup to restore"),
        {
          level: "warning",
          tags: {
            component: "impersonation",
            operation: "end",
            outcome: "no-backup",
          },
        },
      );
      setImpersonationError(
        "Impersonation ended, but your admin session couldn't be restored. Please sign in again.",
      );
    } else if (!restored) {
      errorReporter.reportMessage(
        "endImpersonation: backup already consumed; admin restored by another tab",
        {
          level: "info",
          tags: {
            component: "impersonation",
            operation: "end",
            outcome: "already-restored",
          },
        },
      );
    }

    // 3. Clear impersonation state
    setState(defaultImpersonationState);

    // 4. Reset ref before navigation (navigation may not return)
    isEndingRef.current = false;

    // 5. Hard navigate to force Amplify re-initialization. Restored → back to
    //    the page we started from; genuinely orphaned → re-login; admin already
    //    restored elsewhere (or restored here without a target) → into the app.
    const targetId = medicalAssistantId ?? physicianId;
    if (restored && targetId && targetType) {
      window.location.href = impersonationReturnPath(targetType, targetId);
    } else if (stillImpersonating) {
      window.location.href = "/login";
    } else {
      window.location.href = "/";
    }
  }, [state, lambda, setState]);

  return {
    impersonationState: state,
    isImpersonating: state.isActive,
    startImpersonation,
    endImpersonation,
  };
}
